import * as XLSX from 'xlsx';

import { DriverParameters, IDriverDate, IDriverParameter, IDriverset, TDriversetParameter } from '../api/models';
import { Filename } from '../services/Filename';

/**
 * Excel round trip for a driver set, so admins can edit one in Excel rather
 * than cell by cell in FINS.
 *
 * The sheet is one header row plus one row per Julian day. There is no column
 * mapping step, unlike the layer importer: columns are matched by name, and a
 * name that doesn't match is reported rather than guessed at.
 */

/** Header of the day column, and the one column an import cannot do without. */
const DAY_HEADER = 'Julian day';

type TIssueKind =
  | 'unreadable'      // not an Excel file, or no sheets
  | 'empty'           // readable, but nothing usable in it
  | 'no-day-column'   // fatal: without days there are no rows
  | 'unknown-column'  // a column we don't recognise, ignored
  | 'missing-column'  // a parameter the sheet doesn't carry
  | 'bad-day'         // not an integer 1..365
  | 'duplicate-day'   // the same day twice
  | 'bad-value';      // a cell that isn't a number

interface IDriversetImportIssue {
  kind: TIssueKind;
  message: string;
}

interface IDriversetImportResult {
  /** Rows read successfully, sorted by day. Empty when `fatal`. */
  dates: IDriverDate[];
  issues: IDriversetImportIssue[];
  /**
   * Nothing usable could be read, so the caller must leave the current driver
   * set alone rather than overwrite it with nothing.
   */
  fatal: boolean;
}

/** Row-level problems are listed individually up to this many, then counted. */
const MAX_REPORTED = 40;

/**
 * Headers match on their label alone. The unit is written in parentheses on
 * export and dropped here, so a degree sign or a superscript mangled by Excel
 * doesn't fail the import; case and surrounding whitespace are ignored too.
 * Everything else must match, which is what makes an unrecognised column
 * something we can report rather than silently drop.
 */
const normaliseHeader = (header: unknown): string =>
  String(header ?? '')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/** "Temperature (°C)". A unit of "-" means unitless, so it is left off. */
const exportHeader = (p: IDriverParameter): string =>
  p.unit && p.unit !== '-' ? `${p.label} (${p.unit})` : p.label;

/** Accepted spellings for each parameter: its label, and its machine name. */
const headerLookup = (): Map<string, TDriversetParameter> => {
  const map = new Map<string, TDriversetParameter>();
  for (const p of DriverParameters) {
    map.set(normaliseHeader(p.label), p.name);
    map.set(p.name.toLowerCase(), p.name);
  }
  return map;
}

const DAY_KEYS = new Set([normaliseHeader(DAY_HEADER), 'day', 'julianday']);

class DriversetExcel {

  /** Write the driver set to an .xlsx file and hand it to the browser. */
  static export = (driverset: IDriverset): void => {
    const headers = [DAY_HEADER, ...DriverParameters.map(exportHeader)];

    // Sorted by day: the editor keeps insertion order, which is awkward to work
    // with in a spreadsheet. Import doesn't care about order either way.
    const rows = [...driverset.dates]
      .sort((a, b) => (a.day ?? 0) - (b.day ?? 0))
      .map(d => [d.day, ...DriverParameters.map(p => d.values?.[p.name] ?? null)]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    worksheet['!cols'] = headers.map(() => ({ wch: 18 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Driver set');

    const name = Filename.sanitize(driverset.name || 'driver-set') || 'driver-set';
    XLSX.writeFile(workbook, `${name}.xlsx`, { compression: true });
  }

  /**
   * Read a driver set from the first sheet of an .xlsx file. Never throws: a
   * file that can't be read comes back as a fatal result with an explanation.
   */
  static import = async (file: File): Promise<IDriversetImportResult> => {
    const issues: IDriversetImportIssue[] = [];
    let rows: unknown[][];

    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) {
        return { dates: [], fatal: true, issues: [{ kind: 'unreadable', message: 'The workbook contains no sheets.' }] };
      }
      rows = XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[sheetName], { header: 1, blankrows: false });
    } catch (e) {
      const detail = e instanceof Error ? e.message : String(e);
      return { dates: [], fatal: true, issues: [{ kind: 'unreadable', message: `The file could not be read as Excel: ${detail}` }] };
    }

    if (rows.length === 0) {
      return { dates: [], fatal: true, issues: [{ kind: 'empty', message: 'The sheet is empty.' }] };
    }

    // -- Header row ---------------------------------------------------------
    // Analysed before the row count is checked, because SheetJS parses almost
    // any file into a one-cell sheet rather than throwing. A header that
    // matches nothing is a much better diagnosis of "this isn't a driver set"
    // than "no data rows".
    const header = rows[0] ?? [];
    const lookup = headerLookup();
    const paramColumns = new Map<number, TDriversetParameter>();
    let dayColumn: number | null = null;

    // A plain loop rather than forEach: assigning dayColumn inside a callback
    // loses the narrowing needed for the null check below.
    for (let i = 0; i < header.length; i++) {
      const text = String(header[i] ?? '').trim();
      if (text === '') continue;  // trailing empty headers aren't worth reporting
      const key = normaliseHeader(text);

      if (DAY_KEYS.has(key)) { dayColumn = i; continue; }

      const param = lookup.get(key);
      if (param === undefined) {
        issues.push({
          kind: 'unknown-column',
          message: `Column ${XLSX.utils.encode_col(i)} ("${text}") was not recognised and has been ignored.`
        });
        continue;
      }
      paramColumns.set(i, param);
    }

    if (dayColumn === null && paramColumns.size === 0) {
      return { dates: [], fatal: true, issues: [{
        kind: 'unreadable',
        message: 'No recognised column headers were found, so this does not look like a driver set sheet. Export one to see the headers it expects.'
      }] };
    }

    if (dayColumn === null) {
      issues.push({
        kind: 'no-day-column',
        message: `No "${DAY_HEADER}" column was found, so nothing could be imported. The current driver set is unchanged.`
      });
      return { dates: [], issues, fatal: true };
    }

    const present = new Set(paramColumns.values());
    for (const p of DriverParameters) {
      if (!present.has(p.name)) {
        issues.push({
          kind: 'missing-column',
          message: `No "${p.label}" column was found, so its values are empty. The driver set cannot be saved until it has at least one.`
        });
      }
    }

    if (rows.length < 2) {
      issues.push({ kind: 'empty', message: 'The sheet has no data rows below its header.' });
      return { dates: [], issues, fatal: true };
    }

    // -- Data rows ----------------------------------------------------------
    const dates: IDriverDate[] = [];
    const seen = new Set<number>();
    let rowIssues = 0;

    const note = (kind: TIssueKind, message: string) => {
      rowIssues++;
      if (rowIssues <= MAX_REPORTED) issues.push({ kind, message });
    }

    for (let r = 1; r < rows.length; r++) {
      const row = rows[r] ?? [];
      const line = r + 1;  // 1-based, as Excel's row numbers read

      const rawDay = row[dayColumn];
      if (rawDay == null || String(rawDay).trim() === '') continue;  // blank row

      const day = Number(rawDay);
      if (!Number.isInteger(day) || day < 1 || day > 365) {
        note('bad-day', `Row ${line}: "${String(rawDay)}" is not a Julian day between 1 and 365. Row skipped.`);
        continue;
      }
      if (seen.has(day)) {
        note('duplicate-day', `Row ${line}: day ${day} appears more than once. Row skipped.`);
        continue;
      }
      seen.add(day);

      const values = {} as IDriverDate['values'];
      for (const p of DriverParameters) values[p.name] = null;

      for (const [column, name] of paramColumns) {
        const raw = row[column];
        if (raw == null || String(raw).trim() === '') continue;  // legitimately blank
        const value = Number(raw);
        if (!isFinite(value)) {
          note('bad-value', `Row ${line}, column ${XLSX.utils.encode_col(column)}: "${String(raw)}" is not a number. Value left empty.`);
          continue;
        }
        values[name] = value;
      }

      dates.push({ day, values });
    }

    if (rowIssues > MAX_REPORTED) {
      issues.push({ kind: 'bad-value', message: `…and ${rowIssues - MAX_REPORTED} further row problems, not listed.` });
    }

    if (dates.length === 0) {
      issues.push({ kind: 'empty', message: 'No usable rows were found, so the current driver set is unchanged.' });
      return { dates: [], issues, fatal: true };
    }

    dates.sort((a, b) => (a.day ?? 0) - (b.day ?? 0));
    return { dates, issues, fatal: false };
  }
}

export { DriversetExcel, IDriversetImportResult, IDriversetImportIssue, DAY_HEADER }
